import { apiUrl, buildAuthHeaders } from '../config/api';
import { getAnonId } from './attribution';

interface QueuedEvent {
  feature: string;
  action: string;
  path?: string;
}

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
// Suppress duplicate (feature+action) events fired within this window (ms).
const DEDUPE_MS = 30_000;
const recent = new Map<string, number>();

/**
 * Maps a route pathname to a canonical feature key. Returns null for routes we
 * don't care to track (keeps event volume scoped to actual product tools).
 */
export function featureForPath(pathname: string): string | null {
  const p = pathname.toLowerCase();
  // Order matters: most specific first.
  if (p.startsWith('/health/cycle')) return 'cycle_tracker';
  if (p.startsWith('/health/appointments')) return 'appointments';
  if (p.startsWith('/health/treatments')) return 'treatments';
  if (p.startsWith('/health/meal-plans') || p.startsWith('/health/nutrition')) return 'nutrition';
  if (p.startsWith('/health/symptoms')) return 'symptoms';
  if (p.startsWith('/health/sleep')) return 'sleep';
  if (p.startsWith('/health/activity')) return 'activity';
  if (p.startsWith('/health/intimate')) return 'intimate';
  if (p.startsWith('/health/vitals')) return 'vitals';
  if (p.startsWith('/health/mental')) return 'mental_health';
  if (p === '/health') return 'health_log';
  // Unmapped /health/* subpages fall through to null rather than inflating health_log.
  if (p.startsWith('/chat')) return 'chat';
  if (p.startsWith('/research')) return 'library';
  if (p.startsWith('/trials')) return 'trials';
  if (p.startsWith('/community')) return 'community';
  if (p.startsWith('/advocacy')) return 'advocacy';
  if (p.startsWith('/quiz') || p.startsWith('/get-started')) return 'quiz';
  return null;
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, 4000);
}

async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  try {
    const headers = await buildAuthHeaders();
    await fetch(apiUrl('/events'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ events, anonId: getAnonId() }),
      keepalive: true, // allow delivery during page unload
    });
  } catch {
    /* analytics is best-effort — never surface errors */
  }
}

/** Records a feature-usage event (batched, deduped, best-effort). */
export function trackEvent(feature: string, action: string = 'view', path?: string) {
  if (!feature) return;
  const key = `${feature}:${action}`;
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_MS) return;
  recent.set(key, now);

  queue.push({ feature, action, path });
  scheduleFlush();
}

/** Tracks a route view if the path maps to a tracked feature. */
export function trackPageView(pathname: string) {
  const feature = featureForPath(pathname);
  if (feature) trackEvent(feature, 'view', pathname);
}

// Flush any pending events when the page is being hidden/closed.
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
  window.addEventListener('pagehide', () => void flush());
}
